import { z } from 'zod/v4';

import { StudioVisualizationType } from '@blockworks/platform/api/research/studio';

import {
    BaseVisualizationArgsSchema,
    ColorPropertiesSchema,
    createBaseGroupSchema,
    createColumnTransform,
    createGroupedVisualizationSchema,
    createRowTransform,
    createVisualizationResponseSchema,
    ExecutionMetadata,
    FormatFieldSchema,
    RowIndexFieldSchema,
    VisualizationParametersSchema,
} from '../validation/schemas';

const CounterGroupSchema = createBaseGroupSchema(
    z.object({
        format: FormatFieldSchema,
        ...ColorPropertiesSchema.shape,
        label: z.string(),
        labelSize: z.string().default('md'),
        column: z.string(), // Will be transformed
        rowIndex: RowIndexFieldSchema,
    }),
);

export const CounterOptionsSchema = createGroupedVisualizationSchema(CounterGroupSchema);

export const createCounterArgsSchema = <TColumns extends string = string>(meta: ExecutionMetadata<TColumns>) => {
    const columnTransform = createColumnTransform(meta);
    const rowIndexTransform = createRowTransform(meta);
    // Apply transform to column fields with proper typing

    return BaseVisualizationArgsSchema.extend({
        type: z.literal(StudioVisualizationType.Counter),
        config: z.object({
            id: z.string().optional(),
            parameters: VisualizationParametersSchema.optional(),
            options: createGroupedVisualizationSchema(
                CounterGroupSchema.extend({
                    rowIndex: rowIndexTransform,
                    column: columnTransform,
                }),
            ),
        }),
    });
};

export const CounterResponseSchema = createVisualizationResponseSchema(StudioVisualizationType.Counter).extend({
    options: CounterOptionsSchema,
    data: z.any(),
});

export type CounterOptions = z.infer<typeof CounterOptionsSchema>;
export type CounterResponse = z.infer<typeof CounterResponseSchema>;

// Export the generic type for the args schema
export type CounterArgsSchema<TColumns extends string = string> = ReturnType<typeof createCounterArgsSchema<TColumns>>;
export type CounterArgs<TColumns extends string = string> = z.infer<CounterArgsSchema<TColumns>>;
